<template>
    <GenericModal
        :modelValue="props.modelValue"
        @update:modelValue="handleModalUpdate"
        :closable="true"
        :closeOnClickOutside="true"
        :closeOnEsc="true"
        :contentPadding="'0'"
        :fullscreenMobile="true"
        :darkMode="true"
    >
        <div class="purchase-stars-modal">
            <div class="modal-header">
                <h2 class="title">{{ $t('add_funds.title') }}</h2>
                <p class="subtitle-text">
                    {{ $t('add_funds.default_subtitle') }}
                </p>
            </div>

            <div v-if="loading" class="loading-message">
                {{ $t('general.loading') }}
            </div>

            <div v-else-if="error" class="error-message">
                {{ $t('general.general-error') }}
            </div>

            <div v-else class="purchase-options-container">
                <RadioOption
                    v-for="(option, index) in purchaseOptionsWithDiscount"
                    :key="option.id || index"
                    :isSelected="selectedOption?.id === option.id"
                    :class="{ 'restricted-option': option.restricted }"
                    @click="selectOption(option)"
                >
                    <div class="option-left">
                        <span class="usd-amount" :class="{ 'restricted-text': option.restricted }">
                            {{ formatDecimal128(option.amount) }}
                            <span class="credit-label">Credits</span>
                        </span>
                    </div>

                    <div class="option-right">
                        <span v-if="!option.restricted" class="price" :class="{ 'restricted-text': option.restricted }">
                            ${{ formatDecimal128(option.price) }}
                            <span class="currency">USD</span>
                        </span>
                        <span
                            class="restricted-badge"
                            v-if="option.restricted"
                        >
                            {{ $t('add_funds.pack_restricted_title') }}
                        </span>
                        <span
                            class="discount-badge"
                            v-else-if="option.calculatedDiscount > 0"
                        >
                            {{
                                $t('add_funds.discount_save', {
                                    discount: option.calculatedDiscount,
                                })
                            }}
                        </span>
                    </div>
                </RadioOption>

                <!-- Inline notice shown when a restricted pack was tapped -->
                <div v-if="showRestrictedNotice" class="restricted-notice">
                    <i class="pi pi-shield" />
                    <span>{{ $t('add_funds.pack_restricted_message') }}</span>
                </div>
            </div>

            <PrimaryButton
                class="continue-button"
                :disabled="!selectedOption || loading || processingPayment"
                @click="handleCheckout"
                :label="$t('add_funds.checkout_button')"
                :loading="processingPayment"
                :labelFontSize="'16px'"
                :labelFontWeight="'600'"
            />
        </div>
    </GenericModal>
</template>

<script setup>
import GenericModal from '@/components/shared/core/GenericModal.vue'
import RadioOption from '@/components/shared/core/RadioOption.vue'
import PrimaryButton from '@/components/shared/buttons/primary-button.vue'
import { ref, onMounted, watch, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import transactionService from '@/services/api/transaction.service'
import { usePaymentStore } from '@/stores/payment.store'
import { usePackStore } from '@/stores/pack.store'
import { useAuthStore } from '@/stores/auth.store'
import { useModalStore } from '@/stores/modal.store'
import { ensureRegistered } from '@/guards'
import { storeToRefs } from 'pinia'
import { formatDecimal128 } from '@/utils'

const { t } = useI18n()
const paymentStore = usePaymentStore()
const packStore = usePackStore()
const authStore = useAuthStore()
const modalStore = useModalStore()

const { purchaseOptions, loading, error } = storeToRefs(packStore)

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue'])
const selectedOption = ref(null)
const processingPayment = ref(false)
const showRestrictedNotice = ref(false)

// New-user detection: account < 48h old
const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000
const isNewUser = computed(() => {
    const createdAt = authStore.user.data?.createdAt
    if (!createdAt) return false
    return Date.now() - new Date(createdAt).getTime() < FORTY_EIGHT_HOURS
})

// Calculate discount percentages based on the smallest pack
const purchaseOptionsWithDiscount = computed(() => {
    if (!purchaseOptions.value || purchaseOptions.value.length === 0) {
        return []
    }

    // Find the smallest pack (reference for calculating discounts)
    const sortedOptions = [...purchaseOptions.value].sort(
        (a, b) => a.amount - b.amount
    )
    const smallestPack = sortedOptions[0]

    // Calculate price per star for the smallest pack
    const smallestPricePerStar =
        parseFloat(formatDecimal128(smallestPack.price)) / smallestPack.amount

    // Calculate discount for each option
    return purchaseOptions.value.map((option) => {
        const currentPricePerStar =
            parseFloat(formatDecimal128(option.price)) / option.amount
        const savings =
            ((smallestPricePerStar - currentPricePerStar) /
                smallestPricePerStar) *
            100

        return {
            ...option,
            calculatedDiscount: Math.ceil(Math.max(0, savings)),
            isSameValue:
                parseFloat(formatDecimal128(option.amount)) ===
                parseFloat(formatDecimal128(option.price)),
            restricted: isNewUser.value && option.amount > 250,
        }
    })
})

// Watch for modelValue changes to prevent opening when payment is in progress
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue && paymentStore.isPaymentInProgress()) {
            // If a payment is in progress, don't open the modal
            console.log(
                'Payment in progress, preventing add funds modal from opening'
            )
            emit('update:modelValue', false)
        }
    }
)

const handleModalUpdate = (newValue) => {
    // If a payment is in progress and trying to open the modal, prevent it
    if (newValue && paymentStore.isPaymentInProgress()) {
        console.log(
            'Payment in progress, preventing add funds modal from opening'
        )
        emit('update:modelValue', false)
        return
    }

    emit('update:modelValue', newValue)
}

const selectOption = (option) => {
    // Block selection of restricted packs for new users
    if (option.restricted) {
        showRestrictedNotice.value = true
        return
    }
    showRestrictedNotice.value = false
    selectedOption.value = option
    if (error.value) {
        packStore.error = null
    }
}

const handleCheckout = async () => {
    if (!selectedOption.value) return

    const authCheck = ensureRegistered(authStore)
    if (!authCheck.pass) {
        // Close the add funds modal
        emit('update:modelValue', false)

        // Persist pending add-funds intent so auth success can reopen this flow.
        try {
            localStorage.setItem('pendingAction', 'addFunds')
            localStorage.setItem('pendingPackId', selectedOption.value.id)
        } catch (e) {
            console.error('Failed to store pending action:', e)
        }

        // Request auth in-place and avoid route redirects from auth modal.
        // Wait one tick so the current modal closes cleanly first.
        await nextTick()
        modalStore.openModal({ darkMode: true, disableRedirect: true })
        return
    }

    processingPayment.value = true
    try {
        // Close the modal before redirecting
        emit('update:modelValue', false)

        // Get the optional tracker from the query parameters and clear it
        const optionalTracker = localStorage.getItem('tfTracker')
        if (optionalTracker) {
            localStorage.removeItem('tfTracker')
        }

        // Get AdCash click ID from localStorage (captured on landing page)
        const adcashClickId = localStorage.getItem('adcashClickId')

        // Create the payment session and redirect to checkout
        await transactionService.createPayin(
            selectedOption.value.id,
            optionalTracker,
            undefined, // paymentProcessor
            undefined, // paymentMethod
            undefined, // amount
            adcashClickId // adcashClickId
        )
    } catch (error) {
        console.error('Error initiating checkout:', error)
    } finally {
        processingPayment.value = false
    }
}

// Watch for purchase options to become available and pre-select one
watch(
    purchaseOptions,
    (newOptions) => {
        if (newOptions.length > 0 && !selectedOption.value) {
            // For new users, prefer $250 pack; otherwise keep the existing $500+ preference
            const preselectOption = isNewUser.value
                ? newOptions.find((opt) => opt.amount === 250) ||
                  newOptions.filter((opt) => opt.amount <= 250).pop() ||
                  newOptions[0]
                : newOptions.find((opt) => opt.amount >= 500) ||
                  newOptions[Math.floor(newOptions.length / 2)]
            if (preselectOption) {
                selectOption(preselectOption)
            }
        }
    },
    { immediate: true }
)

onMounted(() => {
    // Fetch packs immediately when modal is mounted
    if (!purchaseOptions.value || purchaseOptions.value.length === 0) {
        packStore.fetchPacks()
    }

    // Also watch for modal opening
    watch(
        () => props.modelValue,
        (isOpen) => {
            if (
                isOpen &&
                (!purchaseOptions.value || purchaseOptions.value.length === 0)
            ) {
                packStore.fetchPacks()
            }
        },
        { immediate: true }
    )
})
</script>

<style scoped>
/* Override modal container max-width for this specific modal */
:deep(.modal-container) {
    max-width: 520px;
    background: #171717;
    border: 1px solid rgba(155, 110, 43, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

@media (max-width: 767px) {
    :deep(.modal-container.fullscreen-mobile) {
        background: transparent;
    }
}

/* Style close button for buy-stars modal */
:deep(.close-button.dark-mode) {
    color: #9b6e2b;
}

:deep(.close-button.dark-mode:hover) {
    background-color: rgba(155, 110, 43, 0.15);
    color: #c9a646;
}

:deep(.close-button.dark-mode i) {
    color: #9b6e2b;
}

/* Center RadioOption content for this modal */
:deep(.radio-option) {
    align-items: center;
}

:deep(.option-content) {
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

/* Main content container */
.purchase-stars-modal {
    color: #ffffff;
    font-family: 'Open Sans', sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 28px 28px;
    position: relative;
    background: linear-gradient(176deg, #252525 0%, #171717 100%);
    min-height: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow-y: auto;
}

/* Extend background to edges on mobile fullscreen */
@media (max-width: 767px) {
    :deep(.modal-container.fullscreen-mobile .modal-content) {
        padding: 0 !important;
        height: 100% !important;
    }

    .purchase-stars-modal {
        padding: 60px 20px 28px;
        height: 100%;
        min-height: 100%;
        margin: 0;
        width: 100%;
    }
}

.modal-header {
    text-align: center;
    margin-bottom: 24px;
    width: 100%;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(155, 110, 43, 0.1);
}

.title {
    font-size: 30px;
    font-weight: 700;
    font-family: 'Meditative', serif;
    line-height: 36px;
    max-width: 320px;
    text-align: center;
    margin: auto;
    margin-bottom: 16px;
    background: linear-gradient(90deg, #9b6e2b 0%, #996923 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.subtitle-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 20px;
    max-width: 360px;
    margin: 0 auto;
    text-wrap-style: balance;
}

.purchase-options-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: auto;
    flex: 1;
    padding-bottom: 12px;
}

.option-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.option-label {
    font-size: 12px;
    line-height: 1;
    margin-left: 10px;
    font-family: 'Open Sans', sans-serif;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
}

.usd-amount {
    font-size: 14px;
    line-height: 1;
    font-family: 'Open Sans', sans-serif;
    font-weight: 500;
    color: #ffffff;
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
}

.credit-label {
    font-size: 12px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
}

.option-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 120px;
    margin-left: auto;
}

.price {
    font-size: 14px;
    font-family: 'Open Sans', sans-serif;
    font-weight: 600;
    color: #ffffff;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    text-align: right;
}

.currency {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
}

.discount-badge {
    background: rgba(155, 110, 43, 0.2);
    border: 1px solid rgba(155, 110, 43, 0.5);
    color: #c9a646;
    font-size: 11px;
    line-height: 1;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
}

.continue-button {
    width: 100%;
    max-width: 400px;
    height: 54px;
    background: linear-gradient(143deg, #9b6e2b 0%, #996923 100%);
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    font-family: 'Basier Circle', sans-serif;
    box-shadow: 0 4px 12px rgba(155, 110, 43, 0.3);
    transition: all 0.2s ease;
    margin-top: auto;
    flex-shrink: 0;
}

@media (max-width: 767px) {
    .continue-button {
        max-width: 100%;
        margin-top: auto;
        margin-bottom: 0;
    }
}

.continue-button:hover:not(:disabled) {
    background: linear-gradient(143deg, #c9a646 0%, #9b6e2b 100%);
    box-shadow: 0 6px 16px rgba(155, 110, 43, 0.4);
    transform: translateY(-1px);
}

.continue-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.loading-message,
.error-message {
    text-align: center;
    padding: 40px 20px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    width: 100%;
}

.error-message {
    color: #ff6b6b;
}

/* --- Restricted pack styles (new-user spend cap) --- */
:deep(.restricted-option) {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.08) !important;
}

:deep(.restricted-option:hover) {
    border-color: rgba(255, 255, 255, 0.08) !important;
    background: transparent !important;
}

.restricted-text {
    color: rgba(255, 255, 255, 0.4) !important;
}

.restricted-lock {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.35);
}

.restricted-badge {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    line-height: 1;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
}

.restricted-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(155, 110, 43, 0.08);
    border: 1px solid rgba(155, 110, 43, 0.2);
    border-radius: 10px;
    margin-top: 4px;
}

.restricted-notice i {
    font-size: 16px;
    color: #c9a646;
    flex-shrink: 0;
    margin-top: 1px;
}

.restricted-notice span {
    font-size: 13px;
    line-height: 18px;
    color: rgba(255, 255, 255, 0.7);
}
</style>
